"use client";

import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useApolloClient } from "@apollo/client/react";
import { useMutation } from "@/api/useMutation";

import { toast } from "@jasperlabs/ui";
import { mutations, useMutationOnError, useCaptureGraphQLError } from "@/api";

import { useSession } from "../session/session";

const logOutContext = createContext();

export const useLogOut = () => {
	const context = useContext(logOutContext);

	if (!context) {
		throw new Error("useLogOut should always be used within a LogOutProvider");
	}

	return context;
};

export const LogOutProvider = ({ children }) => {
	const { removeSession, sessionToken } = useSession();
	const client = useApolloClient();
	const captureMutationError = useCaptureGraphQLError();
	const [mutate, { loading: isLoading, error }] = useMutationOnError(
		captureMutationError,
		useMutation(mutations.DESTROY_SESSION),
	);

	const logOut = useCallback(() => {
		mutate({
			variables: { token: sessionToken },
		});
		removeSession();
		client.clearStore();
		toast.success("You have successfully logged out.");
	}, [client, mutate, removeSession, sessionToken]);

	const value = useMemo(
		() => ({
			logOut,
			isLoggingOut: isLoading,
			logOutError: error,
		}),
		[error, isLoading, logOut],
	);

	return (
		<logOutContext.Provider value={value}>{children}</logOutContext.Provider>
	);
};
